<template>
  <PageChrome>
    <template #title>
      <span class="eyebrow">Insights</span>
      <button class="favToggle" :class="{ active: favoritesOnly }" @click="favoritesOnly = !favoritesOnly" :title="favoritesOnly ? 'Show all cards' : 'Show favorites only'">
        <AppIcon :name="favoritesOnly ? 'star_filled' : 'star'" :size="18" />
      </button>
    </template>

    <template #actions>
      <label class="btn secondary">
        <input type="file" accept=".json" @change="handleImport" hidden />
        Import
      </label>
    </template>

    <div class="canvasPage">
      <!-- Loading -->
      <div v-if="loading && cards.length === 0" class="emptyState">Loading...</div>

      <!-- Empty -->
      <div v-else-if="!loading && cards.length === 0" class="emptyState">
        <p>No insights yet.</p>
        <p class="emptyHint">Ask the chat to create a report — e.g., "Show me reply rates by sales play as a bar chart"</p>
      </div>

      <!-- Card stack -->
      <TransitionGroup v-else name="card" tag="div" class="cardStack">
        <CanvasCard
          v-for="card in cards"
          :key="card.id"
          :card="card"
          :class="{ isNew: newCardIds.has(card.id) }"
          :refreshing="refreshingId === card.id"
          @toggle-favorite="toggleFavorite"
          @refresh="handleRefresh"
          @export="handleExport"
          @delete="handleDelete"
        >
          <CanvasTable
            v-if="cardDetails[card.id]?.card_type === 'table'"
            :columns="cardDetails[card.id]?.result_data?.columns || []"
            :rows="cardDetails[card.id]?.result_data?.rows || []"
            :row-entity="cardDetails[card.id]?.display_config?.row_entity || null"
          />
          <CanvasChart
            v-else-if="cardDetails[card.id]?.card_type === 'chart'"
            :rows="cardDetails[card.id]?.result_data?.rows || []"
            :display-config="cardDetails[card.id]?.display_config || {}"
          />
          <CanvasMetric
            v-else-if="cardDetails[card.id]?.card_type === 'metric'"
            :columns="cardDetails[card.id]?.result_data?.columns || []"
            :rows="cardDetails[card.id]?.result_data?.rows || []"
            :display-config="cardDetails[card.id]?.display_config || {}"
          />
          <div v-else class="loadingCard">Loading...</div>
        </CanvasCard>
      </TransitionGroup>
    </div>
  </PageChrome>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'
import AppIcon from '../../components/AppIcon.vue'
import PageChrome from '../components/PageChrome.vue'
import CanvasCard from '../../components/canvas/CanvasCard.vue'
import CanvasTable from '../../components/canvas/CanvasTable.vue'
import CanvasChart from '../../components/canvas/CanvasChart.vue'
import CanvasMetric from '../../components/canvas/CanvasMetric.vue'
import {
  fetchCanvasCards,
  fetchCanvasCard,
  refreshCanvasCard,
  updateCanvasCard,
  deleteCanvasCard,
  downloadCanvasCardCsv,
  exportCanvasCards,
  importCanvasCards,
  type CanvasCardSummary,
  type CanvasCardDetail,
} from '../../services/canvas'
const cards = ref<CanvasCardSummary[]>([])
const cardDetails = ref<Record<string, CanvasCardDetail>>({})
const loading = ref(true)
const favoritesOnly = ref(false)
const refreshingId = ref<string | null>(null)
const newCardIds = ref<Set<string>>(new Set())
const knownCardIds = ref<Set<string>>(new Set())

const POLL_INTERVAL_MS = 5_000

let pollTimer: ReturnType<typeof setInterval> | null = null

async function loadCards() {
  loading.value = true
  try {
    cards.value = await fetchCanvasCards({ favorites: favoritesOnly.value })
    // Track which IDs we know about for new-card detection.
    knownCardIds.value = new Set(cards.value.map(c => c.id))
    await loadDetailsForCards(cards.value)
  } finally {
    loading.value = false
  }
}

async function loadDetailsForCards(cardList: CanvasCardSummary[]) {
  const idsToFetch = cardList.filter(c => !cardDetails.value[c.id])
  if (idsToFetch.length === 0) return
  const details = await Promise.all(
    idsToFetch.map(c => fetchCanvasCard(c.id).catch(() => null))
  )
  for (const d of details) {
    if (d) cardDetails.value[d.id] = d
  }
}

async function poll() {
  try {
    const fresh = await fetchCanvasCards({ favorites: favoritesOnly.value })

    // Detect new cards (IDs we haven't seen before).
    const freshIds = new Set(fresh.map(c => c.id))
    const brandNew: string[] = []
    for (const id of freshIds) {
      if (!knownCardIds.value.has(id)) {
        brandNew.push(id)
      }
    }

    if (brandNew.length > 0) {
      // Mark new cards for the entrance animation.
      for (const id of brandNew) {
        newCardIds.value.add(id)
      }
      // Clear the "new" highlight after the animation plays.
      setTimeout(() => {
        for (const id of brandNew) {
          newCardIds.value.delete(id)
        }
      }, 1500)
    }

    // Update the list (even if no new cards — handles deletions from other tabs).
    cards.value = fresh
    knownCardIds.value = freshIds
    await loadDetailsForCards(fresh)
  } catch {
    // Swallow poll errors — next tick will retry.
  }
}

function startPolling() {
  stopPolling()
  pollTimer = setInterval(poll, POLL_INTERVAL_MS)
}

function stopPolling() {
  if (pollTimer) {
    clearInterval(pollTimer)
    pollTimer = null
  }
}

watch(favoritesOnly, () => loadCards())

onMounted(async () => {
  await loadCards()
  startPolling()
})

onBeforeUnmount(() => {
  stopPolling()
})

async function toggleFavorite(card: CanvasCardSummary) {
  const updated = await updateCanvasCard(card.id, {
    favorited: !card.favorited_at,
  })
  const idx = cards.value.findIndex(c => c.id === card.id)
  if (idx >= 0) {
    cards.value[idx] = { ...cards.value[idx], favorited_at: updated.favorited_at }
  }
  if (cardDetails.value[card.id]) {
    cardDetails.value[card.id] = updated
  }
}

async function handleRefresh(card: CanvasCardSummary) {
  refreshingId.value = card.id
  const minSpin = new Promise(r => setTimeout(r, 600))
  try {
    const [updated] = await Promise.all([refreshCanvasCard(card.id), minSpin])
    cardDetails.value[card.id] = updated
    const idx = cards.value.findIndex(c => c.id === card.id)
    if (idx >= 0) {
      cards.value[idx] = { ...cards.value[idx], row_count: updated.row_count, updated_at: updated.updated_at }
    }
  } finally {
    refreshingId.value = null
  }
}

async function handleDelete(card: CanvasCardSummary) {
  await deleteCanvasCard(card.id)
  cards.value = cards.value.filter(c => c.id !== card.id)
  knownCardIds.value.delete(card.id)
  delete cardDetails.value[card.id]
}

async function handleExport(card: CanvasCardSummary) {
  await downloadCanvasCardCsv(card.id, card.title)
}

async function handleImport(event: Event) {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  if (!file) return
  const text = await file.text()
  try {
    const data = JSON.parse(text)
    const cardsData = Array.isArray(data) ? data : [data]
    await importCanvasCards(cardsData)
    await loadCards()
  } catch {
    alert('Invalid JSON file')
  }
  input.value = ''
}

</script>

<style scoped>
.canvasPage {
  max-width: 960px;
}

.cardStack {
  display: flex;
  flex-direction: column;
  gap: var(--space-4, 16px);
  position: relative;
}

/* ── TransitionGroup: new cards slide down + fade in ── */
.card-enter-active {
  transition: all 0.4s ease-out;
}
.card-enter-from {
  opacity: 0;
  transform: translateY(-12px);
}
.card-enter-to {
  opacity: 1;
  transform: translateY(0);
}
.card-leave-active {
  transition: all 0.25s ease-in;
}
.card-leave-from {
  opacity: 1;
  transform: translateY(0);
}
.card-leave-to {
  opacity: 0;
  transform: translateY(8px);
}
/* Smooth reflow when items enter/leave */
.card-move {
  transition: transform 0.35s ease;
}

/* Brief highlight glow on brand-new cards */
.isNew {
  animation: newCardGlow 1.2s ease-out;
}
@keyframes newCardGlow {
  0% {
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.5);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(99, 102, 241, 0);
  }
}

.emptyState {
  text-align: center;
  padding: 120px 20px;
  color: var(--color-text-secondary, var(--muted));
  font-size: 18px;
}
.emptyHint {
  font-size: 15px;
  margin-top: 12px;
  max-width: 480px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.5;
}

.loadingCard {
  padding: 24px;
  text-align: center;
  color: var(--color-text-secondary, var(--muted));
}

.favToggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: 6px;
  padding: 2px;
  border: none;
  background: none;
  color: var(--color-text-tertiary, #999);
  cursor: pointer;
  border-radius: 4px;
  transition: color 0.15s;
}
.favToggle:hover {
  color: var(--color-text-primary, #1a1a1a);
}
.favToggle.active {
  color: #f59e0b;
}

.eyebrow {
  font-size: 18px;
  font-weight: 600;
}
</style>
